import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import {
  ChartLine, House, Leaf, Bug, Virus, FlowerLotus,
  Drop, CalendarDots, Wrench, Gear, Handshake, EnvelopeSimple,
  Wind, Flask, SignOut, Moon, Sun, UserCircle, ShieldCheck,
  Lightning, FileText, TShirt, Storefront, Crosshair
} from '@phosphor-icons/react';
import { TroubleshootHelpButton } from '../components/TroubleshootHelp';
import { ExploreTurfPulse } from '../components/ExploreTurfPulse';
import { ThisWeekModule } from '../components/ThisWeekModule';
import { SensorPlacementCTA } from '../components/SensorPlacementCTA';

const FEATURES = [
  { path: '/app', label: 'Dashboard', desc: 'GDD tracking & weather', icon: ChartLine, color: 'bg-emerald-600' },
  { path: '/property', label: 'My Property', desc: 'Zones & lawn management', icon: House, color: 'bg-amber-600' },
  { path: '/pgr', label: 'PGR Department', desc: 'Growth regulator timing', icon: Leaf, color: 'bg-green-700' },
  { path: '/weed', label: 'Weed Control', desc: 'Herbicide products & timing', icon: FlowerLotus, color: 'bg-lime-700' },
  { path: '/pest', label: 'Pest Management', desc: 'Insecticide products & timing', icon: Bug, color: 'bg-orange-700' },
  { path: '/disease', label: 'Disease Mgmt', desc: 'Fungicide products & timing', icon: Virus, color: 'bg-red-700' },
  { path: '/soil-analysis', label: 'Soil Analysis', desc: 'Test results & recommendations', icon: Flask, color: 'bg-stone-600' },
  { path: '/spray-forecast', label: 'Spray Forecast', desc: '5-day wind & spray windows', icon: Wind, color: 'bg-sky-700' },
  { path: '/irrigation', label: 'Irrigation', desc: 'Watering schedules & tracking', icon: Drop, color: 'bg-blue-700' },
  { path: '/sensors/placement', label: 'Sensor Placement', desc: 'Map Ecowitt probes — unlock Auto-Fire', icon: Crosshair, color: 'bg-cyan-800' },
  { path: '/season-report', label: 'Season Report', desc: 'Export spray logs & EPA data', icon: FileText, color: 'bg-cyan-700' },
  { path: '/calendar', label: 'Seasonal Calendar', desc: 'Year-round lawn care schedule', icon: CalendarDots, color: 'bg-violet-700' },
  { path: '/equipment', label: 'Equipment Log', desc: 'Mower & tool maintenance', icon: Wrench, color: 'bg-zinc-600' },
  { path: '/spreader-settings', label: 'Spreader Settings', desc: 'Calibration & community rates', icon: Gear, color: 'bg-teal-700' },
  { path: '/partners', label: 'Partners', desc: 'Local lawn care providers', icon: Handshake, color: 'bg-indigo-700' },
  { path: '/email-preferences', label: 'Alert Settings', desc: 'GDD & spray email alerts', icon: EnvelopeSimple, color: 'bg-rose-700' },
  { path: '/merch', label: 'Merch Lab', desc: 'Vote on gear & concepts', icon: TShirt, color: 'bg-emerald-700' },
  { path: '/marketplace', label: 'Group Buys', desc: 'Split bulk products', icon: Storefront, color: 'bg-amber-700' },
];

export const WelcomeHubPage = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'nav', 'dashboard']);

  const firstName = user?.name?.split(' ')[0] || 'there';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('common:goodMorning', 'Good morning') : hour < 17 ? t('common:goodAfternoon', 'Good afternoon') : t('common:goodEvening', 'Good evening');

  const tierLabel = {
    free: t('common:free'),
    homeowner_seasonal: t('common:homeownerSeasonal', 'Homeowner Seasonal'),
    enthusiast_annual: t('common:enthusiastAnnual', 'Enthusiast Annual'),
  }[user?.subscription_tier] || t('common:free');

  return (
    <div className="min-h-screen bg-background" data-testid="welcome-hub-page">
      {/* Header */}
      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <TurfPulseLogoSVG animated size={32} />
            <span className="font-semibold text-lg tracking-tight hidden sm:inline">TurfPulse</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="hub-theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/profile')}
              data-testid="hub-profile-btn"
              aria-label="Profile"
            >
              <UserCircle size={20} />
            </Button>
            {user?.role === 'admin' && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/admin')}
                data-testid="hub-admin-btn"
                aria-label="Admin panel"
              >
                <ShieldCheck size={20} />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={async () => { await logout(); navigate('/'); }}
              data-testid="hub-logout-btn"
              aria-label="Log out"
            >
              <SignOut size={20} />
            </Button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Greeting */}
        <div className="mb-8 sm:mb-12">
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-foreground"
            data-testid="hub-greeting"
          >
            {greeting}, <span className="font-semibold">{firstName}</span>
          </h1>
          <p className="mt-2 text-muted-foreground text-sm sm:text-base">
            {tierLabel} {t('common:plan', 'plan')} &middot; {t('nav:readyToManage', 'Ready to manage your lawn')}
          </p>
        </div>

        {/* This Week on Your Lawn — top-priority Smart Plan action */}
        <div className="mb-6">
          <ThisWeekModule />
        </div>

        {/* Guardian: Sensor Placement CTA (self-hides if not Ecowitt-connected or already confirmed) */}
        <div className="mb-6">
          <SensorPlacementCTA />
        </div>

        {/* Explore TurfPulse — Feature Discovery Checklist */}
        <div className="mb-8">
          <ExploreTurfPulse />
        </div>

        {/* Quick-access grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
          {FEATURES.map(({ path, label, desc, icon: Icon, color }) => (
            <Card
              key={path}
              className="group cursor-pointer border border-border/50 hover:border-primary/40 hover:shadow-md transition-all duration-200 bg-card"
              onClick={() => navigate(path)}
              data-testid={`hub-card-${path.replace('/', '')}`}
            >
              <CardContent className="p-4 sm:p-5">
                <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${color} text-white mb-3 group-hover:scale-105 transition-transform`}>
                  <Icon size={22} weight="duotone" />
                </div>
                <h3 className="font-medium text-sm sm:text-base text-foreground leading-tight">{label}</h3>
                <p className="text-xs text-muted-foreground mt-1 leading-snug hidden sm:block">{desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Help link */}
        <div className="flex justify-end mt-4">
          <TroubleshootHelpButton />
        </div>

        {/* Upgrade banner for free users */}
        {user?.subscription_tier === 'free' && user?.role !== 'admin' && (
          <div
            className="mt-8 sm:mt-10 rounded-xl border border-primary/20 bg-primary/5 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            data-testid="hub-upgrade-banner"
          >
            <div>
              <h3 className="font-semibold text-base text-foreground">Unlock the full toolkit</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Upgrade to Homeowner or Enthusiast for weed control, pest management, soil analysis, and more.
              </p>
            </div>
            <Button
              onClick={() => navigate('/app')}
              className="shrink-0"
              data-testid="hub-upgrade-btn"
            >
              View Plans
            </Button>
          </div>
        )}

        {/* End of main content */}
      </main>
    </div>
  );
};
